// SPDX-License-Identifier: GPL-3.0-or-later
// Perk catalog, perk fold and the run bounds.
//
// A perk changes scoring, timers or the ball save only. It never changes physics.
// The fold runs at each pick. Play reads the result and never changes it.

import { clamp } from "../animation.ts";
import { TIMES } from "../rules/constants.ts";
import type {
  PerkDef,
  PerkEffect,
  PerkEffects,
  PerkId,
  PerkStacks,
  ScoreChannel,
} from "./types.ts";

/** The bounds of the run. Every value is fixed and public. */
export const RUN_LIMITS = {
  /** Upper clamp of the scale of a channel. The lower clamp is 1. */
  MAX_CHANNEL_SCALE: 3,
  LAUNCH_SAVE_MIN_MS: 3_000,
  LAUNCH_SAVE_MAX_MS: 16_000,
  COMBO_WINDOW_MIN_MS: 3_000,
  COMBO_WINDOW_MAX_MS: 6_000,
  MODE_TIME_MIN_SCALE: 0.75,
  MODE_TIME_MAX_BONUS_MS: 10_000,
  HURRY_MS: 20_000,
  HURRY_MAX_MS: 25_000,
  FRENZY_MS: 15_000,
  FRENZY_MAX_MS: 20_000,
  BONUS_MULT_START_MAX: 2,
  MAX_EXTRA_BALLS_GAME: 2,
  MAX_EXTRA_BALLS_STAGE: 1,
} as const;

/** The score channels. The fold makes one scale for each. */
const CHANNELS: readonly ScoreChannel[] = [
  "switch",
  "ramp",
  "orbit",
  "combo",
  "jackpot",
  "mode",
  "event",
  "other",
];

/** The perk catalog. The order is the draft order. */
export const PERKS: readonly PerkDef[] = [
  {
    id: "steady_hands",
    label: "STEADY HANDS",
    text: "The launch ball save lasts 3 s more.",
    effect: { kind: "launch_save_ms", ms: 3_000 },
    maxStacks: 2,
  },
  {
    id: "ramp_runner",
    label: "RAMP RUNNER",
    text: "Ramp shots score 25% more.",
    effect: { kind: "channel_scale", channel: "ramp", scale: 1.25 },
    maxStacks: 2,
  },
  {
    id: "orbit_ace",
    label: "ORBIT ACE",
    text: "Orbit shots score 25% more.",
    effect: { kind: "channel_scale", channel: "orbit", scale: 1.25 },
    maxStacks: 2,
  },
  {
    id: "pop_star",
    label: "POP STAR",
    text: "Bumpers, slingshots, the spinner, lanes and targets score 50% more.",
    effect: { kind: "channel_scale", channel: "switch", scale: 1.5 },
    maxStacks: 1,
  },
  {
    id: "chain_link",
    label: "CHAIN LINK",
    text: "The combo window lasts 1 s more.",
    effect: { kind: "combo_window_ms", ms: 1_000 },
    maxStacks: 2,
  },
  {
    id: "jackpot_hunter",
    label: "JACKPOT HUNTER",
    text: "Jackpots and ASK MODE shots score 25% more.",
    effect: { kind: "channel_scale", channel: "jackpot", scale: 1.25 },
    maxStacks: 1,
  },
  {
    id: "overtime",
    label: "OVERTIME",
    text: "Each timed mode lasts 5 s more.",
    effect: { kind: "mode_time_ms", ms: 5_000 },
    maxStacks: 2,
  },
  {
    id: "hurry_hold",
    label: "HOLD THE LINE",
    text: "The hurry-up timer lasts 5 s more.",
    effect: { kind: "hurry_time_ms", ms: 5_000 },
    maxStacks: 1,
  },
  {
    id: "frenzy_fuel",
    label: "FRENZY FUEL",
    text: "The frenzy lasts 5 s more.",
    effect: { kind: "frenzy_time_ms", ms: 5_000 },
    maxStacks: 1,
  },
  {
    id: "bonus_boost",
    label: "BONUS BOOST",
    text: "Each ball starts with a 2x bonus multiplier.",
    effect: { kind: "bonus_mult_start", value: 2 },
    maxStacks: 1,
  },
  {
    id: "mode_master",
    label: "MODE MASTER",
    text: "Timed mode completion awards score 25% more.",
    effect: { kind: "channel_scale", channel: "mode", scale: 1.25 },
    maxStacks: 1,
  },
  {
    id: "spare_bot",
    label: "SPARE BOT",
    text: "A spare SigmaBot adds 1 ball to play when the game resumes. Up to 4 balls play at once.",
    effect: { kind: "add_ball", balls: 1 },
    maxStacks: 2,
  },
];

/** The fold of the perks, before the clamp. Each field is mutable here only. */
interface FoldAcc {
  channelScale: Record<ScoreChannel, number>;
  launchSaveBonusMs: number;
  comboWindowBonusMs: number;
  modeTimeBonusMs: number;
  hurryBonusMs: number;
  frenzyBonusMs: number;
  bonusMultStart: number;
}

/** A run with no perk. */
export function emptyStacks(): PerkStacks {
  const stacks = {} as PerkStacks;
  for (const def of PERKS) stacks[def.id] = 0;
  return stacks;
}

/** True when the run holds fewer copies of the perk than its max. */
export function canTake(stacks: Readonly<PerkStacks>, id: PerkId): boolean {
  const def = PERKS.find((p) => p.id === id);
  return def !== undefined && copiesOf(stacks, def) < def.maxStacks;
}

/** The copies of a perk that the run holds. A bad value counts as 0. */
function copiesOf(stacks: Readonly<PerkStacks>, def: PerkDef): number {
  const n = stacks[def.id];
  if (!Number.isInteger(n) || n < 0) return 0;
  return Math.min(n, def.maxStacks);
}

/** Add one perk effect to the fold, for `copies` copies. */
function applyEffect(acc: FoldAcc, effect: PerkEffect, copies: number): void {
  switch (effect.kind) {
    case "channel_scale":
      acc.channelScale[effect.channel] *= effect.scale ** copies;
      return;
    case "launch_save_ms":
      acc.launchSaveBonusMs += effect.ms * copies;
      return;
    case "combo_window_ms":
      acc.comboWindowBonusMs += effect.ms * copies;
      return;
    case "mode_time_ms":
      acc.modeTimeBonusMs += effect.ms * copies;
      return;
    case "hurry_time_ms":
      acc.hurryBonusMs += effect.ms * copies;
      return;
    case "frenzy_time_ms":
      acc.frenzyBonusMs += effect.ms * copies;
      return;
    case "bonus_mult_start":
      acc.bonusMultStart = Math.max(acc.bonusMultStart, effect.value);
      return;
    case "add_ball":
      // The director adds the ball at the pick. The fold has nothing to sum.
      return;
  }
}

/** The scale of each channel with no perk. */
function unitScales(): Record<ScoreChannel, number> {
  const scales = {} as Record<ScoreChannel, number>;
  for (const channel of CHANNELS) scales[channel] = 1;
  return scales;
}

// The clamp is a guard. The catalog values stay inside the caps.
/** Clamp each fold value to the run bounds. */
function clampEffects(acc: FoldAcc): PerkEffects {
  const channelScale = unitScales();
  for (const channel of CHANNELS) {
    channelScale[channel] = clamp(acc.channelScale[channel], 1, RUN_LIMITS.MAX_CHANNEL_SCALE);
  }
  const launchRoom = RUN_LIMITS.LAUNCH_SAVE_MAX_MS - TIMES.ballSaveLaunchMs;
  const comboRoom = RUN_LIMITS.COMBO_WINDOW_MAX_MS - TIMES.comboWindowMs;
  return {
    channelScale,
    launchSaveBonusMs: clamp(acc.launchSaveBonusMs, 0, launchRoom),
    comboWindowBonusMs: clamp(acc.comboWindowBonusMs, 0, comboRoom),
    modeTimeBonusMs: clamp(acc.modeTimeBonusMs, 0, RUN_LIMITS.MODE_TIME_MAX_BONUS_MS),
    hurryBonusMs: clamp(acc.hurryBonusMs, 0, RUN_LIMITS.HURRY_MAX_MS - RUN_LIMITS.HURRY_MS),
    frenzyBonusMs: clamp(acc.frenzyBonusMs, 0, RUN_LIMITS.FRENZY_MAX_MS - RUN_LIMITS.FRENZY_MS),
    bonusMultStart: clamp(acc.bonusMultStart, 1, RUN_LIMITS.BONUS_MULT_START_MAX),
  };
}

/** The sum of the perks that the run holds, inside the run bounds. */
export function foldPerks(stacks: Readonly<PerkStacks>): PerkEffects {
  const acc: FoldAcc = {
    channelScale: unitScales(),
    launchSaveBonusMs: 0,
    comboWindowBonusMs: 0,
    modeTimeBonusMs: 0,
    hurryBonusMs: 0,
    frenzyBonusMs: 0,
    bonusMultStart: 1,
  };
  for (const def of PERKS) {
    const copies = copiesOf(stacks, def);
    if (copies > 0) applyEffect(acc, def.effect, copies);
  }
  return clampEffects(acc);
}
